import { Box, Typography, useMediaQuery } from '@mui/material';
import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import AgreementsServiceClientImpl from 'src/api/agreementsService/client';
import { SignAgreementsDto } from 'src/api/basketService';
import { ReactComponent as BackArrow } from 'src/assets/svg/back-arrow.svg';
import { ReactComponent as SageLogo } from 'src/assets/svg/sage-logo.svg';
import { Button } from 'src/components/button';
import { OrderedList } from 'src/components/ordered-list';
import { PaymentOptionBanner } from 'src/components/payment-option-banner';
import { Section } from 'src/components/section';
import { AcknowledgeSummaryBox } from 'src/features/agreements/acknowledge-summary-box';
import { AgreementDrawer } from 'src/features/agreements/agreement-drawer';
import { PaymentPlanMobileOverview } from 'src/features/payment-plan';
import { RoomCard } from 'src/features/room-card';
import { BookingDetailsContentShort } from 'src/features/your-details/booking-details/booking-details-short';
import { Currency } from 'src/shared/enum/Currency';
import {
    selectBookingDetails,
    selectBuilding,
    selectIsHouseOfSocial,
    selectRoom,
} from 'src/store/slices/booking-slice';
import { selectLanguage } from 'src/store/slices/language-slice';
import { selectPromotion } from 'src/store/slices/payment-plan-slice';
import { DevelopmentSlug } from 'src/store/types/enums/DevelopmentSlug';
import { LicenceCountries } from 'src/store/types/enums/LicenceCountries';
import { theme } from 'src/styles/theme';
import { validateEmail } from 'src/utils/validators/validate-email';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';
import { selectUserDetails } from 'src/store/slices/user-slice';
import { selectBasket, selectBasketProducts } from 'src/store/slices/basket-slice';
import { selectIsDualOccupantFlow } from 'src/store/slices/dual-occupant-slice';
import { getTheme } from 'src/utils/getTheme';

import { Agreement } from './agreement';
import { bookingTerms } from './content/booking-terms';
import { EmailValidationState } from './enum/EmailValidationState';
import { styles } from './styles';
import { BookingCountry } from './types';
import { houseOfSocialBookingTerms } from './content/house-of-social-booking-terms';

import type { AgreementsPageProps, TermsItem } from './types';

enum AgreementType {
    Terms = 1,
    Licence = 2,
}

export const AgreementsPage: FC<AgreementsPageProps> = ({
    onGoBack,
    hasRoommates,
    onSubmit,
    loading,
    error,
    adminFee,
    hideBackButton,
}) => {
    const { t } = useTranslation();
    const { isHosTheme } = getTheme();
    const [termsCheckboxVal, setTermsCheckboxVal] = useState(false);
    const [licenceCheckboxVal, setLicenceCheckboxVal] = useState(false);
    const [termsEmailAddress, setTermsEmailAddress] = useState('');
    const [licenceEmailAddress, setLicenceEmailAddress] = useState('');
    const [licenceContent, setLicenceContent] = useState<string>('');
    const [termsEmailValidationState, setTermsEmailValidationState] = useState(
        EmailValidationState.VALID
    );
    const [licenceEmailValidationState, setLicenceEmailValidationState] = useState(
        EmailValidationState.VALID
    );
    const [showAgreementInfo, setShowAgreementInfo] = useState(false);
    const [currentDrawer, setCurrentDrawer] = useState<AgreementType>(AgreementType.Terms);
    const [currentAgreement, setCurrentAgreement] = useState<AgreementType>(AgreementType.Terms);
    const [hasDiscount, setHasDiscount] = useState<boolean>(false);

    const roomData = useSelector(selectRoom);
    const selectedLanguage = useSelector(selectLanguage);
    const building = useSelector(selectBuilding);
    const bookingDetails = useSelector(selectBookingDetails);
    const selectedUser = useSelector(selectUserDetails);
    const currentBasket = useSelector(selectBasket);
    const basketProducts = useSelector(selectBasketProducts);
    const isDualOccupantFlow = useSelector(selectIsDualOccupantFlow);
    const isHouseOfSocial = useSelector(selectIsHouseOfSocial);
    const isRebooker = currentBasket.options.rebooker;

    const isSpanishBuilding = building?.spanish_tax_applicable ?? false;

    const noPaymentRequired = !!(
        !roomData?.advance_payment_weeks ||
        isRebooker ||
        isDualOccupantFlow ||
        !adminFee
    );

    const totalAgreements = 2;

    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

    const promotion = useSelector(selectPromotion);

    useEffect(() => {
        if (promotion) {
            setHasDiscount(true);
        }
    }, [promotion]);

    useEffect(() => {
        window.dataLayer.push({
            event: 'go_to_agreements',
        });

        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        let hasParking = false;
        let licenceCountry = LicenceCountries.UK;

        let roomCode;

        if (isDualOccupantFlow && roomData) {
            roomCode = roomData.room_code;
        } else if (basketProducts) {
            roomCode = basketProducts[0].room_code;
        } else if (roomData) {
            roomCode = roomData.room_code;
        }

        if (!building || !roomCode) return;

        switch (building.development_slug) {
            case DevelopmentSlug.VITA_STUDENT_BIRMINGHAM:
            case DevelopmentSlug.PORTLAND_HOUSE:
            case DevelopmentSlug.VITA_STUDENT_GLASGOW:
            case DevelopmentSlug.ZED_ALLEY:
            case DevelopmentSlug.VITA_STUDENT_LEWISHAM:
            case DevelopmentSlug.VITA_STUDENT_NOTTINGHAM:
            case DevelopmentSlug.VITA_STUDENT_AT_PORTSWOOD_SOUTHAMPTON:
            case DevelopmentSlug.LEEDS_PORTLAND_CRESCENT:
                hasParking = true;
                break;
            default:
                break;
        }

        switch (true) {
            case building.irish_licence_type:
                licenceCountry = LicenceCountries.IRELAND;
                break;
            case building.scottish_licence_type:
                licenceCountry = LicenceCountries.SCOTLAND;
                break;
            case building.welsh_licence_type:
                licenceCountry = LicenceCountries.WALES;
                break;
            case building.spanish_licence_type:
                licenceCountry = LicenceCountries.SPAIN;
                break;
            default:
                break;
        }

        new AgreementsServiceClientImpl()
            .getAgreement(hasParking, licenceCountry, roomCode, isHouseOfSocial)
            .then((data) => {
                setLicenceContent(data);
            });
    }, [building, basketProducts, isDualOccupantFlow]);

    const currency = bookingDetails?.currency as Currency;

    const handleTermsCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
        setTermsCheckboxVal(event.target.checked);
    };

    const handleLicenceCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
        setLicenceCheckboxVal(event.target.checked);
    };

    const handleTermsEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (!selectedUser) {
            throw new Error(ErrorMessage.NO_USER_SELECTED);
        }
        const email = event.target.value.trim().toLowerCase();
        setTermsEmailAddress(email);

        const emailEntered = !!email.length;

        if (!emailEntered) {
            setTermsEmailValidationState(EmailValidationState.VALID);
            return;
        }

        const isValidEmail = email === '' || validateEmail(email);
        const emailMatches = email === selectedUser.email;

        if (!isValidEmail) {
            setTermsEmailValidationState(EmailValidationState.INVALID_EMAIL);
            return;
        }

        setTermsEmailValidationState(
            emailMatches ? EmailValidationState.VALID : EmailValidationState.MISMATCHED_EMAIL
        );
    };

    const handleLicenceEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!selectedUser) {
            throw new Error(ErrorMessage.NO_USER_SELECTED);
        }

        const email = event.target.value.trim().toLowerCase();
        setLicenceEmailAddress(email);

        const emailEntered = !!email.length;

        if (!emailEntered) {
            setLicenceEmailValidationState(EmailValidationState.VALID);
            return;
        }

        const isValidEmail = email === '' || validateEmail(email);
        const emailMatches = email === selectedUser.email;

        if (!isValidEmail) {
            setLicenceEmailValidationState(EmailValidationState.INVALID_EMAIL);
            return;
        }

        setLicenceEmailValidationState(
            emailMatches ? EmailValidationState.VALID : EmailValidationState.MISMATCHED_EMAIL
        );
    };

    const isEmailValidTerms = termsEmailValidationState === EmailValidationState.VALID;
    const isEmailValidLicence = licenceEmailValidationState === EmailValidationState.VALID;

    const isBookingTermsSigned =
        termsCheckboxVal &&
        termsEmailValidationState &&
        termsEmailAddress !== '' &&
        isEmailValidTerms;

    const isLicenceAgreementSigned =
        licenceCheckboxVal &&
        licenceEmailValidationState &&
        licenceEmailAddress !== '' &&
        isEmailValidLicence;

    const isButtonDisabled =
        !isBookingTermsSigned ||
        !isLicenceAgreementSigned ||
        !isEmailValidTerms ||
        !isEmailValidLicence;

    const handleOnClick = async () => {
        if (!isBookingTermsSigned || !isLicenceAgreementSigned) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        const agreementsData: SignAgreementsDto = {
            licence_agreement_signed: licenceCheckboxVal,
            booking_terms_signed: termsCheckboxVal,
        };

        await onSubmit(agreementsData);
    };

    const showAgreementDrawer = () => setShowAgreementInfo((prev) => !prev);

    const goToNextDrawer = () => {
        setCurrentDrawer(AgreementType.Licence);
        setCurrentAgreement(AgreementType.Licence);
    };

    const skipDrawer = () => setShowAgreementInfo(false);

    const bookingTermsVariant = isHouseOfSocial
        ? houseOfSocialBookingTerms
        : bookingTerms[isSpanishBuilding ? BookingCountry.Spain : BookingCountry.UK];

    const bookingLang = bookingTermsVariant.languages[selectedLanguage];

    const bookingTermsContent = (
        <Box>
            <Typography variant="h2">{bookingLang.header}</Typography>
            <ol>
                {bookingLang.items.map((section: TermsItem) => (
                    <li key={section.text} css={styles.listItem}>
                        <Typography variant="h3" css={styles.header}>
                            {section.header}
                        </Typography>

                        {section.intro && (
                            <Typography variant="h4" css={styles.intro}>
                                {section.intro}
                            </Typography>
                        )}

                        {section.introUnstyledText && (
                            <Typography
                                variant="body1"
                                css={styles.intro}
                                dangerouslySetInnerHTML={{ __html: section.introUnstyledText }}
                            />
                        )}

                        {section.text && (
                            <Typography
                                variant="body1"
                                dangerouslySetInnerHTML={{ __html: section.text }}
                            />
                        )}

                        {section.orderedList && <OrderedList list={section.orderedList} />}

                        {section.outro && <Typography variant="body1">{section.outro}</Typography>}
                    </li>
                ))}
            </ol>
        </Box>
    );

    const bookingTermsAndConditions = (
        <Agreement
            checkboxVal={termsCheckboxVal}
            emailAddress={termsEmailAddress}
            handleEmailChange={handleTermsEmailChange}
            handleCheckboxChange={handleTermsCheckboxChange}
            emailValidationState={termsEmailValidationState}
            title="agreements_booking_terms_and_conditions_title"
            subtitle="agreements_please_read_t&c"
            content={bookingTermsContent}
            checkboxContent="agreements_booking_terms_and_conditions_checkbox_content"
            emailContent="agreements_booking_terms_and_conditions_email_content"
            submitButton={
                <Button
                    variant="contained"
                    onClick={goToNextDrawer}
                    css={styles.cta}
                    disabled={!isBookingTermsSigned}
                >
                    {t('agreement_next_agreement')}
                </Button>
            }
        />
    );

    const licenceAgreement = (
        <Agreement
            checkboxVal={licenceCheckboxVal}
            emailAddress={licenceEmailAddress}
            handleEmailChange={handleLicenceEmailChange}
            handleCheckboxChange={handleLicenceCheckboxChange}
            emailValidationState={licenceEmailValidationState}
            title="agreements_licence_agreement_title"
            subtitle="agreements_please_read_licence_agreement"
            // eslint-disable-next-line react/no-danger
            content={<div dangerouslySetInnerHTML={{ __html: licenceContent }} />}
            checkboxContent="agreements_licence_agreement_checkbox_content"
            emailContent="agreements_licence_agreement_email_content"
            submitButton={
                <Button
                    variant="contained"
                    onClick={skipDrawer}
                    css={styles.cta}
                    disabled={!isLicenceAgreementSigned}
                >
                    {t('agreements_submit')}
                </Button>
            }
        />
    );

    return (
        <Box css={styles.layout}>
            {!isSmallScreen && !hideBackButton && (
                <Box css={styles.back} onClick={onGoBack}>
                    <BackArrow css={styles.icon} />
                    <Typography>{t('agreements_back_button_content')}</Typography>
                </Box>
            )}

            <Section header={t('agreements_title')}>
                {!isSmallScreen ? (
                    <Typography variant="body1">{t('agreements_sub_text')}</Typography>
                ) : (
                    <Typography variant="body3">{t('agreements_subheader')}</Typography>
                )}

                {isSmallScreen && (
                    <Box css={styles.wrapBox}>
                        <AcknowledgeSummaryBox
                            signed={isBookingTermsSigned}
                            content={t('agreements_summary_box_booking_terms_conditions')}
                            showAgreementDrawer={() => {
                                setCurrentDrawer(AgreementType.Terms);
                                setCurrentAgreement(AgreementType.Terms);
                                showAgreementDrawer();
                            }}
                        />
                        <AcknowledgeSummaryBox
                            signed={isLicenceAgreementSigned}
                            content={t('agreements_summary_box_licence_agreement')}
                            showAgreementDrawer={() => {
                                setCurrentDrawer(AgreementType.Licence);
                                setCurrentAgreement(AgreementType.Licence);
                                showAgreementDrawer();
                            }}
                        />
                    </Box>
                )}

                {/* Agreement drawers */}
                <AgreementDrawer
                    open={showAgreementInfo}
                    closeFn={showAgreementDrawer}
                    currentAgreement={currentAgreement}
                    totalAgreements={totalAgreements}
                >
                    {currentDrawer === AgreementType.Terms
                        ? bookingTermsAndConditions
                        : licenceAgreement}
                </AgreementDrawer>

                {/* Display content on bigger screens */}
                {!isSmallScreen && (
                    <>
                        {bookingTermsAndConditions}
                        {licenceAgreement}
                    </>
                )}
            </Section>

            <Section header={t('booking_summary_title')} style={styles.mobileSummary}>
                <Box css={styles.roomCardWrapper}>
                    <RoomCard roomData={roomData}>
                        <Box css={styles.bookingDetailsWrapper}>
                            <BookingDetailsContentShort />
                        </Box>
                    </RoomCard>
                </Box>

                <PaymentPlanMobileOverview
                    hasDiscount={hasDiscount}
                    showTotalPayment
                    currency={currency}
                />
            </Section>

            {!noPaymentRequired && isSmallScreen && isHosTheme && (
                <Box css={styles.paymentOptionBannerWrapper}>
                    <PaymentOptionBanner showPaymentOptions />
                </Box>
            )}

            <Button
                variant="contained"
                css={styles.cta}
                disabled={!isSmallScreen && isButtonDisabled}
                onClick={handleOnClick}
                loading={loading}
            >
                {!noPaymentRequired
                    ? t('agreements_cta_payment')
                    : t('agreements_cta_complete_booking')}
            </Button>

            {error && (
                <Box css={styles.errorBox}>
                    <Typography color={theme.palette.primary.main} variant="subtitle1">
                        {error.message}
                    </Typography>
                </Box>
            )}

            <Box css={styles.sageWrapper}>
                {!noPaymentRequired && (
                    <Box>
                        <SageLogo />
                        <Typography>{t('agreements_payment_content')}</Typography>
                    </Box>
                )}

                {isSmallScreen && (
                    <Box css={[styles.back, styles.extraPadding]} onClick={onGoBack}>
                        {hasRoommates
                            ? t('agreements_back_to_roommates')
                            : t('agreements_back_content')}
                    </Box>
                )}
            </Box>
            {isSmallScreen && !noPaymentRequired && !isHosTheme && <PaymentOptionBanner />}
        </Box>
    );
};
